import React from 'react';
import styled, { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import i18n from 'i18next';
import { withTranslation } from 'react-i18next';

import DonutChart from 'common/charts/GoogleCharts/DonutChart';
import BarChart from 'common/charts/GoogleCharts/BarChart';
import WidgetChartContainer from 'common/components/molecules/WidgetChartContainer';
import LoadingSvg from 'common/images/LoadingSvg';
import Chart from 'react-google-charts';
import H3 from 'common/typography/H3/H3';
import SecondaryText from 'common/typography/SecondaryText/SecondaryText';
import { getDevicesResourceName } from '../../constants/adminDashboard.i18n';
import { eventAnalyticsMetadata, widgetTypes } from '../constants/eventAnalytics.constants';
import { translateChartData } from '../eventAnalytics.chartData';
import * as eventAnalyticsActions from '../eventAnalytics.actions';

const ChartContainer = styled.div`
  background-color: white;
  height: 220px;
  border-radius: 4px;
`;

const EventAnalyticsWidget = (props) => {
  const { t } = props;
  const eventAnalyticsState = props.eventAnalytics;
  const data = eventAnalyticsState[props.analytics_type];
  const metadata = eventAnalyticsMetadata(props.theme)[props.analytics_type];
  // Guarded because this is now the first read of `metadata`, above the early returns:
  // an unrecognized analytics_type leaves it undefined, and the loading branch below still
  // has to render.
  const widgetType = metadata && metadata.widgetType;

  // Localizing at render (rather than at fetch) lets a language switch relabel without
  // a refetch, but the charts redraw whenever `data` changes identity. Memoize so a
  // re-render that changes neither the rows nor the language doesn't force a redraw.
  // Keyed on `widgetType` rather than `metadata`: eventAnalyticsMetadata() builds a fresh
  // object every render, which would defeat the memo.
  // Declared above the early returns below: hooks must run on every render.
  const chartData = React.useMemo(
    () => translateChartData(widgetType, t, data),
    [widgetType, t, data]
  );

  if (eventAnalyticsState.loading) {
    return (
      <>
        <WidgetChartContainer className="ps-align-center">
            <LoadingSvg />
        </WidgetChartContainer>
      </>
    );
  }
  if (eventAnalyticsState.total === 0 || !data) {
    return (
      <>
        <WidgetChartContainer className="ps-align-center">
            <SecondaryText>{metadata.title}</SecondaryText>
            <H3>{i18n.t('common.noEventDataToShow', { defaultValue: 'No Event data to show' })}</H3>
        </WidgetChartContainer>
      </>
    );
  }
  if (data.error) {
    return (
    <>
      <div>{i18n.t('common.errors.errorLoadingEventAnalytics', { defaultValue: 'Error loading Event Analytics' })}</div>
    </>
    );
  }
  return (
    <ChartContainer>
      {widgetType === widgetTypes.DONUT_CHART && (
        <DonutChart
          title={metadata.title}
          data={chartData}
          resourceName={getDevicesResourceName()}
          colors={metadata.colors}
          formatData
          {...props}
        />
      )}
      {widgetType === widgetTypes.BAR_CHART && (
        <BarChart
          height={180}
          title={metadata.title}
          data={chartData}
          colors={metadata.colors}
          orientation="horizontal"
          chartArea={{ width: '80%', left: '15%' }}
          formatData
          {...props}
        />
      )}
      {widgetType === widgetTypes.HISTOGRAM && (
        <Chart
          chartType="Histogram"
          // Row labels are backend-generated English event descriptions; only headers localize.
          data={chartData}
          width="100%"
          height="100%"
          options={metadata}
        />
      )}
    </ChartContainer>
  );
};

const mapStateToProps = state => ({
  adminDashboard: state.adminDashboard,
  eventAnalytics: state.eventAnalytics
});

const mapDispatchToProps = dispatch => ({
  eventAnalyticsActions: bindActionCreators(eventAnalyticsActions, dispatch)
});

EventAnalyticsWidget.propTypes = {
  first: PropTypes.bool,
  titlePaddingBottom: PropTypes.string,
  analytics_type: PropTypes.string,
  showTitle: PropTypes.bool,
  filters: PropTypes.array,
  t: PropTypes.func.isRequired
};

EventAnalyticsWidget.defaultProps = {
  first: false,
  titlePaddingBottom: null,
  analytics_type: null,
  showTitle: true,
  filters: []
};

// Unconnected component exported under a distinct name so tests can mount it with plain
// props and no store. The `Base` suffix follows the convention in DigitalTwinWidget.js and
// keeps import/no-named-as-default quiet at the default-import sites.
export { EventAnalyticsWidget as EventAnalyticsWidgetBase };

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(withTranslation()(EventAnalyticsWidget)));
